/*
  DynamicEntryPage allows a user to:
  - Select a field or group of fields from a dropdown
  - Enter values dynamically based on that selection
  - Submit data with a date to the /api/pending route
*/

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import HelpVideoEmbed from '../components/HelpVideoEmbed';
import { get, post } from '../api/client';
import { fieldGroups, fieldLabels } from '../fieldConfig';
import { getHelpVideoArea, getVideosForSection, hasPlayableVideo } from '../config/helpVideos';

const formatDateReadable = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const DynamicEntryPage = () => {
  const navigate = useNavigate();

  // Form state
  const [selectedOption, setSelectedOption] = useState('');
  const [formData, setFormData] = useState({});
  const [sectionComment, setSectionComment] = useState('');
  const [date, setDate] = useState('');
  const [error, setError] = useState('');
  const [hasExistingData, setHasExistingData] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [sectionHelpOpen, setSectionHelpOpen] = useState(false);

  // When date + section are selected, fetch existing data for that section and pre-populate
  useEffect(() => {
    if (!date || !selectedOption) {
      setHasExistingData(false);
      return;
    }

    const fetchExisting = async () => {
      try {
        const { ok, status, data } = await get(`/api/pending/${date}`);
        if (status === 404) {
          setHasExistingData(false);
          setFormData({});
          setSectionComment('');
          return;
        }
        if (!ok) return;

        const fields = fieldGroups[selectedOption] || [];
        const sectionEntries = (Array.isArray(data) ? data : []).filter(e => fields.includes(e.type));

        if (sectionEntries.length > 0) {
          const prefill = {};
          let comment = '';
          sectionEntries.forEach(entry => {
            prefill[entry.type] = entry.value;
            if (entry.comment) comment = entry.comment;
          });
          setFormData(prefill);
          setSectionComment(comment);
          setHasExistingData(true);
        } else {
          setFormData({});
          setSectionComment('');
          setHasExistingData(false);
        }
      } catch (err) {
        setHasExistingData(false);
      }
    };

    fetchExisting();
  }, [date, selectedOption]);

  // Handle value changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /** True if the user has entered anything that would be lost when changing date or section */
  const hasUnsavedInput = () => {
    if ((sectionComment || '').trim()) return true;
    return Object.keys(formData).some((k) => {
      const v = formData[k];
      return v !== '' && v !== undefined && v !== null;
    });
  };

  // Reset when changing section (useEffect will re-fetch for new section)
  const handleSectionChange = (e) => {
    const next = e.target.value;
    if (next !== selectedOption && selectedOption && hasUnsavedInput()) {
      if (!window.confirm('Switch section? Any numbers or comments you entered for the current section will be cleared.')) {
        return;
      }
    }
    setSelectedOption(next);
    setFormData({});
    setSectionComment('');
    setSectionHelpOpen(false);
  };

  const handleDateChange = (e) => {
    const next = e.target.value;
    if (next !== date && date && selectedOption && hasUnsavedInput()) {
      if (!window.confirm('Change the date? Your current entries may be replaced when data loads for the new date.')) {
        return;
      }
    }
    setDate(next);
  };

  // Show confirmation modal when user clicks Submit
  const handleSubmitClick = (e) => {
    e.preventDefault();
    setError('');
    if (!date || !selectedOption) {
      setError('Date and a field selection are required.');
      return;
    }
    setShowSubmitConfirm(true);
  };

  // Actual submit (called after user confirms)
  const handleConfirmSubmit = async () => {
    setShowSubmitConfirm(false);
    setError('');

    const fields = fieldGroups[selectedOption] || [];
    const comment = (sectionComment || '').trim();
    const group = selectedOption;

    try {
      for (const field of fields) {
        const payload = {
          date,
          type: field,
          value: Number(formData[field] || 0),
          comment,
          group,
          replace: hasExistingData,
        };

        const response = await post('/api/pending', payload);

        if (!response.ok) {
          throw new Error(`Failed to submit ${field}`);
        }
      }

      // Capture data before clearing, then navigate to Thank You page
      const submittedDate = date;
      const submittedFields = { ...formData };
      if (comment) submittedFields['Section comment'] = comment;

      setFormData({});
      setSectionComment('');
      setSelectedOption('');
      setDate('');
      setHasExistingData(false);

      navigate('/thank-you', { state: { date: submittedDate, fields: submittedFields } });
    } catch (err) {
      setError('Submission failed.');
    }
  };

  // Render inputs based on selection
  const selectedFields = fieldGroups[selectedOption] || [];
  const overviewVideo = getHelpVideoArea('dynamic-entry')?.videos?.find((v) => v.id === 'overview');
  const sectionVideos = getVideosForSection(selectedOption);
  const sectionHelpVideo = sectionVideos[0] || null;

  return (
    <div className="entry-page">
      <PageHeader title="Add Group Data" section="gatherings" />
      <div style={{ backgroundColor: 'var(--cr-bg-warm)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid var(--cr-border)', width: '100%', maxWidth: '520px' }}>
        <button
          type="button"
          id="add-group-data-instructions-toggle"
          aria-expanded={instructionsOpen}
          aria-controls="add-group-data-instructions"
          onClick={() => setInstructionsOpen((o) => !o)}
          style={{
            width: '100%',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
            color: 'var(--cr-text)',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.35rem 0.5rem',
          }}
        >
          <span aria-hidden>{instructionsOpen ? '▼' : '▶'}</span>
          <strong>Instructions</strong>
          <span style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)', fontWeight: 400 }}>
            {instructionsOpen ? '(hide)' : '(tap to expand)'}
          </span>
        </button>
        {instructionsOpen && (
          <div id="add-group-data-instructions" style={{ marginTop: '0.75rem' }}>
            <ol style={{ paddingLeft: '1.2rem', margin: '0 0 0.85rem 0' }}>
              <li><strong>Select the Friday night date</strong> you are reporting for.</li>
              <li><strong>Choose one section</strong> from the dropdown (e.g., Large Gp/Children/Workers, Donations, Teens). Only one section per submission.</li>
              <li><strong>Enter the numbers</strong> for that section. If data already exists for this date and section, you will be prompted—submitting will replace it.</li>
              <li><strong>Optionally add a comment</strong> for the Ministry Lead (e.g., notable events or notes from the night).</li>
              <li><strong>Click Submit.</strong> The Ministry Lead will review and combine all sections into the final report.</li>
            </ol>
            {overviewVideo && hasPlayableVideo(overviewVideo) && (
              <div style={{ marginBottom: '0.75rem' }}>
                <HelpVideoEmbed video={overviewVideo} compact />
              </div>
            )}
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--cr-text-muted)' }}>
              Prefer a walkthrough? Open{' '}
              <Link
                to="/help-videos?area=dynamic-entry"
                style={{ color: 'var(--cr-link)', textDecoration: 'underline' }}
              >
                Help Videos — Add Group Data
              </Link>
              {' '}for a short explainer per section.
            </p>
          </div>
        )}
      </div>
      <form onSubmit={handleSubmitClick}>
        <div className="form-group">
          <label htmlFor="dynamic-entry-date">Date:</label>
          <div style={{ position: 'relative', width: '100%', marginTop: '0.25rem' }}>
            <div
              id="dynamic-entry-date-display"
              aria-hidden="true"
              style={{
                padding: '0.35rem 0.5rem',
                border: '1px solid var(--cr-border)',
                borderRadius: '8px',
                backgroundColor: 'var(--cr-surface)',
                color: date ? 'var(--cr-text)' : 'var(--cr-text-muted)',
                fontSize: '1rem',
              }}
            >
              {date ? formatDateReadable(date) : 'Tap to select date'}
            </div>
            <input
              id="dynamic-entry-date"
              type="date"
              value={date}
              onChange={handleDateChange}
              onClick={e => e.target.showPicker?.()}
              required
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                margin: 0,
                zIndex: 1,
              }}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="dynamic-entry-section">Choose Section:</label>
          <select id="dynamic-entry-section" value={selectedOption} onChange={handleSectionChange} required>
            <option value="">-- Select --</option>
            {Object.keys(fieldGroups).map(group => (
              <option key={group} value={group}>{group}</option>
            ))}
          </select>
        </div>

        {sectionHelpVideo && (
          <div style={{
            backgroundColor: 'var(--cr-bg-warm)',
            padding: '0.85rem 1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            border: '1px solid var(--cr-border)',
            width: '100%',
            maxWidth: '520px',
          }}>
            <button
              type="button"
              aria-expanded={sectionHelpOpen}
              aria-controls="dynamic-entry-section-help"
              onClick={() => setSectionHelpOpen((o) => !o)}
              style={{
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--cr-text)',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.35rem 0.5rem',
              }}
            >
              <span aria-hidden>{sectionHelpOpen ? '▼' : '▶'}</span>
              <strong>How to enter this section</strong>
              <span style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)', fontWeight: 400 }}>
                {sectionHelpOpen ? '(hide)' : '(tap to expand)'}
              </span>
            </button>
            {sectionHelpOpen && (
              <div id="dynamic-entry-section-help" style={{ marginTop: '0.75rem' }}>
                <HelpVideoEmbed video={sectionHelpVideo} compact />
                <p style={{ margin: '0.65rem 0 0 0', fontSize: '0.9rem', color: 'var(--cr-text-muted)' }}>
                  <Link
                    to={`/help-videos?area=dynamic-entry&video=${encodeURIComponent(sectionHelpVideo.id)}`}
                    style={{ color: 'var(--cr-link)', textDecoration: 'underline' }}
                  >
                    Open in Help Videos
                  </Link>
                </p>
              </div>
            )}
          </div>
        )}

        {hasExistingData && (
          <div style={{
            backgroundColor: 'var(--cr-bg-warm)',
            color: 'var(--cr-text)',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            border: '1px solid var(--cr-border)'
          }}>
            <strong>Note:</strong> This section already has data for this date.
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.95rem' }}>
              The fields below show the current values. Submitting will replace them.
              Please review and only submit if you intend to update.
            </p>
          </div>
        )}

        {selectedFields.map(field => (
          <div className="form-group" key={field}>
            <label htmlFor={`dynamic-entry-field-${field}`}>{fieldLabels[field] ?? field}:</label>
            <input
              id={`dynamic-entry-field-${field}`}
              type="number"
              value={formData[field] ?? ''}
              onChange={e => handleInputChange(field, e.target.value)}
              required
            />
          </div>
        ))}

        <div className="form-group" style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
          <label htmlFor="dynamic-entry-comment">Comments for this section (optional):</label>
          <textarea
            id="dynamic-entry-comment"
            value={sectionComment}
            onChange={e => setSectionComment(e.target.value)}
            placeholder="e.g., Notable events, issues, or notes from the night..."
            rows={2}
            style={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              padding: '0.35rem 0.5rem',
              borderRadius: '8px',
              border: '1px solid var(--cr-border)',
              resize: 'vertical',
            }}
          />
        </div>

        <button type="submit">Submit</button>
      </form>
      {error && <p style={{ color: 'var(--cr-text)', fontSize: '0.9rem' }}>{error}</p>}

      {showSubmitConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setShowSubmitConfirm(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--cr-surface)',
              borderRadius: '12px',
              padding: '1.5rem',
              maxWidth: '400px',
              width: '100%',
              border: '1px solid var(--cr-border)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 1rem 0', color: 'var(--cr-text)', fontSize: '1.2rem' }}>Are you sure?</h2>
            {hasExistingData && (
              <p style={{ margin: '0 0 0.75rem 0', color: 'var(--cr-text)', fontSize: '0.95rem', fontWeight: 600 }}>
                This will replace existing data already submitted for this date and section.
              </p>
            )}
            <p style={{ margin: '0 0 1rem 0', color: 'var(--cr-text-muted)', fontSize: '0.95rem' }}>
              Please review the numbers below before submitting for {formatDateReadable(date)} ({selectedOption}):
            </p>
            <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
              {selectedFields.map(field => (
                <div key={field} style={{ marginBottom: '0.35rem', fontSize: '0.95rem', color: 'var(--cr-text)' }}>
                  <strong>{fieldLabels[field] ?? field}:</strong> {formData[field] ?? 0}
                </div>
              ))}
              {(sectionComment || '').trim() && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.95rem', fontStyle: 'italic', color: 'var(--cr-text-muted)' }}>
                  <strong>Comment:</strong> {(sectionComment || '').trim()}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowSubmitConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: 'var(--cr-text)',
                  border: '1px solid var(--cr-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--cr-button)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Yes, submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicEntryPage;